import { createSelector } from '@reduxjs/toolkit';

import { Seal, Signature, User } from '@paragon/contract-library';
import { naturalSortByProperty } from '@paragon/utilities';

import { RootState } from '../store';

const getLoggedInUser = (state: RootState) => state.logInData.loggedInUser;
const getUsers = (state: RootState) => state.userData.users;
const getUserToEditEmail = (state: RootState) => state.userData.userToEdit;
const getSeals = (state: RootState) => state.sealsAndSignatures.seals;
const getSignatures = (state: RootState) => state.sealsAndSignatures.signatures;

export const userList = createSelector(getUsers, (users) => Object.values(users));

export interface UserFromSelector extends User {
  readonly seals: readonly Seal[];
  readonly signatures: readonly Signature[];
  readonly isRoot: boolean;
}

export const loggedInUser = createSelector(
  getLoggedInUser,
  getUsers,
  getSeals,
  getSignatures,
  (userFromJwt, users, seals, signatures) => {
    if (userFromJwt == null) return null;
    const userFromState = users[userFromJwt.email];
    if (userFromState == null) return null;
    const userData: UserFromSelector = {
      ...userFromState,
      seals: seals.filter((seal) => seal.userEmail === userFromState.email),
      signatures: signatures.filter((signature) => signature.userEmail === userFromState.email),
      isRoot: userFromState.permissions.includes('root'),
    };
    return userData;
  },
);

export const userToEdit = createSelector(
  getUsers,
  getUserToEditEmail,
  getSeals,
  getSignatures,
  (users, userToEditEmail, seals, signatures) => {
    if (users === null || userToEditEmail === null) return null;
    const rawUserToEdit = users[userToEditEmail];
    const userData: UserFromSelector = {
      ...rawUserToEdit,
      seals: seals.filter((seal) => seal.userEmail === rawUserToEdit.email),
      signatures: signatures.filter((signature) => signature.userEmail === rawUserToEdit.email),
      isRoot: rawUserToEdit.permissions.includes('root'),
    };
    return userData;
  },
);

export const getUsersSortedByFirstName = createSelector(
  (state: RootState) => state.userData.users,
  (users) => {
    const usersArray = Object.values(users);
    return naturalSortByProperty(usersArray, (user) => user.firstName);
  },
);
